import { useEffect } from "react";
import {
  Machine,
  assign,
  EventObject,
  ErrorPlatformEvent,
  ActionTypes,
} from "xstate";
import { useMachine } from "@xstate/react";
import isEqual from "lodash/isEqual";
import invariant from "invariant";

import { StripeSdk } from "@zapier/stripe";

import { logToSentry } from "@zapier/checkout/esm/utils/logToSentry";
import {
  confirmPaymentIntent,
  isStripeError,
} from "@zapier/checkout/esm/utils";
import { invoicePreviewQuery_account_invoicePreview_paymentIntent as PaymentIntentType } from "@zapier/checkout/esm/graphql-types";

import { usePrevious } from "../usePrevious";

type Context = {
  isSdkInitialized: boolean;
  paymentIntent?: PaymentIntentType | null;

  //@ts-ignore
  error?: stripe.Error | Error;
};

type Schema = {
  states: {
    idle: {};
    init: {};
    errorInitializing: {};
    waitForCheckout: {};
    confirmPaymentIntent: {};
    errorConfirmingPaymentIntent: {};
    submit: {};
    errorSubmitting: {};
    done: {};
  };
};

export enum State {
  idle = "idle",
  init = "init",
  errorInitializing = "errorInitializing",
  waitForCheckout = "waitForCheckout",
  // eslint-disable-next-line no-shadow
  confirmPaymentIntent = "confirmPaymentIntent",
  errorConfirmingPaymentIntent = "errorConfirmingPaymentIntent",
  submit = "submit",
  errorSubmitting = "errorSubmitting",
  done = "done",
}

type Event =
  | {
      type: "resetWithPaymentIntent";
      paymentIntent: PaymentIntentType | null | undefined;
    }
  | { type: "init" }
  | { type: "submit" }
  | { type: "retry" }
  | { type: "cancel" };

const isErrorPlatformEvent = (
  event: EventObject
): event is ErrorPlatformEvent =>
  event.type.startsWith(ActionTypes.ErrorPlatform);

const paymentIntentsMachine = Machine<Context, Schema, Event>(
  {
    id: "paymentIntentsMachine",
    initial: "idle",
    context: {
      isSdkInitialized: false,
      paymentIntent: undefined,
      error: undefined,
    },
    states: {
      idle: {
        on: {
          init: [
            {
              target: "waitForCheckout",
              cond: "hasIntentAndSdkIsInitialized",
            },
            {
              target: "init",
              cond: "hasPaymentIntent",
            },
            { target: "idle" },
          ],
          submit: { target: "submit", cond: "paymentIntentNotRequired" },
        },
      },
      init: {
        invoke: {
          src: "eagerLoadStripeSdk",
          onDone: { target: "waitForCheckout", actions: "setIsSdkInitialized" },
          onError: "errorInitializing",
        },
      },
      errorInitializing: {
        entry: ["logInitializationError"],
        type: "final",
      },
      waitForCheckout: {
        on: {
          submit: "confirmPaymentIntent",
        },
      },
      confirmPaymentIntent: {
        invoke: {
          src: "confirmPaymentIntent",
          onDone: "submit",
          onError: {
            target: "errorConfirmingPaymentIntent",
            actions: [
              assign({
                error: (_context, event) => event.data,
              }),
            ],
          },
        },
      },
      errorConfirmingPaymentIntent: {
        entry: ["maybeLogConfirmationError"],
        on: {
          retry: "confirmPaymentIntent",
          submit: "confirmPaymentIntent",
        },
      },
      submit: {
        invoke: {
          src: "submitPayment",
          onDone: "done",
          onError: {
            target: "errorSubmitting",
            actions: [
              assign({
                error: (_context, event) => event.data,
              }),
            ],
          },
        },
      },
      // Errors that happen when submitting should get picked up by our changePlanError alert in ChangePlan.tsx
      errorSubmitting: {
        on: {
          submit: "submit",
        },
      },
      done: {
        type: "final",
      },
    },
    on: {
      resetWithPaymentIntent: {
        target: "#paymentIntentsMachine",
        actions: ["resetMachine"],
      },
    },
  },
  {
    guards: {
      hasPaymentIntent: context => Boolean(context.paymentIntent),
      paymentIntentNotRequired: context => !context.paymentIntent,
      hasIntentAndSdkIsInitialized: context =>
        Boolean(context.paymentIntent) && context.isSdkInitialized,
    },
    actions: {
      logInitializationError: (_context, event) => {
        if (isErrorPlatformEvent(event))
          logToSentry(`paymentIntentsMachine.${event.type}`, event.data);
      },
      maybeLogConfirmationError: context => {
        if (
          isStripeError(context.error) &&
          // Card errors are errors specific to card details or errors processing a user's card, declines, etc
          context.error.type !== "card_error"
        ) {
          logToSentry(`paymentIntentsMachine.confirm.error`, context.error);
        }
      },
      //@ts-ignore
      setIsSdkInitialized: assign({
        isSdkInitialized: () => true,
      }),
      resetMachine: assign((_context, event) => {
        return event.type === "resetWithPaymentIntent"
          ? {
              paymentIntent: event.paymentIntent,
              error: undefined,
            }
          : {};
      }),
    },
    services: {
      eagerLoadStripeSdk: async () => {
        const result = await StripeSdk.get();
        if (result.isErr()) throw result.error;
      },
      confirmPaymentIntent: async context => {
        invariant(
          context.paymentIntent,
          "We must have a payment intent to confirm."
        );
        const result = await confirmPaymentIntent(context.paymentIntent);
        if (result.isErr()) throw result.error;
        return result.value;
      },
    },
  }
);

const usePaymentIntentsMachine = (
  paymentIntent: PaymentIntentType | null | undefined,
  onChangePlan: () => Promise<void | null>
) => {
  //@ts-ignore
  const piMachine = useMachine(paymentIntentsMachine, {
    context: {
      paymentIntent,
      error: undefined,
    },
    services: {
      submitPayment: onChangePlan,
    },
  });

  const [state, send] = piMachine;
  const previousPaymentIntent = usePrevious(paymentIntent);
  const paymentIntentDidChange = !isEqual(previousPaymentIntent, paymentIntent);

  useEffect(() => {
    /**
     * When the payment intent changes (for example, if the user changes any details about their plan, we generate a new payment intent),
     * we need to reset the payment intent state machine with those new details. We do that
     * by setting the PI context with the new one, and resetting back to the initial state.
     */
    if (paymentIntentDidChange) {
      //@ts-ignore
      send({ type: "resetWithPaymentIntent", paymentIntent });
      /**
       * After the resetting to the initial state, we re-initialize the machine (which will go through the Stripe SDK init if needed, etc).
       */
      send("init");
    }
  }, [paymentIntentDidChange, send, paymentIntent]);

  return {
    isVerifyingPayment: state.matches(State.confirmPaymentIntent),
    hasFatalError: state.matches(State.errorInitializing),
    hasPaymentConfirmationError: state.matches(
      State.errorConfirmingPaymentIntent
    ),
    retryPayment: () => send("retry"),
    submit: () => send("submit"),
    state,
    send,
  };
};

export { paymentIntentsMachine, usePaymentIntentsMachine };
